// redux/slices/backupSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getSiteBackups,
  getAllSitesBackups,
  createBackup,
  restoreBackup,
  deleteBackup,
  downloadBackup,
} from "../../services/Backup";

// Get site backups thunk
export const fetchSiteBackups = createAsyncThunk(
  "/backups/sites",
  async ({ siteId, page = 1, limit = 10 }, { rejectWithValue }) => {
    try {
      const response = await getSiteBackups(siteId, { page, limit });
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  },
);

// Get all sites backups thunk
export const fetchAllSitesBackups = createAsyncThunk(
  "/backups/all",
  async (
    { siteId = "", backupType = "", status = "", page = 1, limit = 10 } = {},
    { rejectWithValue },
  ) => {
    try {
      const params = {
        page,
        limit,
        ...(siteId && { siteId }),
        ...(backupType && { backupType }),
        ...(status && { status }),
      };
      const response = await getAllSitesBackups(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  },
);

// Create backup thunk
export const createNewBackup = createAsyncThunk(
  "/backups/create",
  async ({ siteId, payload }, { rejectWithValue }) => {
    try {
      const response = await createBackup(siteId, payload);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

// Restore backup thunk
export const restoreSiteBackup = createAsyncThunk(
  "/backups/restore",
  async (
    { backupId, restoreFiles = true, restoreDatabase = true },
    { rejectWithValue },
  ) => {
    try {
      const response = await restoreBackup(backupId, {
        restoreFiles,
        restoreDatabase,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

// Delete backup thunk
export const deleteSiteBackup = createAsyncThunk(
  "/backups/delete",
  async (backupId, { rejectWithValue }) => {
    try {
      const response = await deleteBackup(backupId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

// Download backup thunk
export const downloadSiteBackup = createAsyncThunk(
  "/backups/download",
  async ({ backupId, fileType = "files" }, { rejectWithValue }) => {
    try {
      const response = await downloadBackup(backupId, fileType);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

const backupSlice = createSlice({
  name: "backup",
  initialState: {
    backups: [],
    allBackups: [],
    loading: false,
    allBackupsLoading: false,
    error: null,
    allBackupsError: null,
    createLoading: false,
    createError: null,
    restoreLoading: false,
    restoreError: null,
    deleteLoading: false,
    deleteError: null,
    downloadLoading: false,
    downloadError: null,
    pagination: {
      currentPage: 1,
      totalPages: 0,
      totalCount: 0,
      hasNext: false,
      hasPrev: false,
    },
    allBackupsPagination: {
      currentPage: 1,
      totalPages: 0,
      totalCount: 0,
      hasNext: false,
      hasPrev: false,
    },
  },
  reducers: {
    clearBackupState: (state) => {
      state.error = null;
      state.allBackupsError = null;
      state.createError = null;
      state.restoreError = null;
      state.deleteError = null;
      state.downloadError = null;
    },
    clearBackupData: (state) => {
      state.backups = [];
      state.allBackups = [];
      state.pagination = {
        currentPage: 1,
        totalPages: 0,
        totalCount: 0,
        hasNext: false,
        hasPrev: false,
      };
      state.allBackupsPagination = {
        currentPage: 1,
        totalPages: 0,
        totalCount: 0,
        hasNext: false,
        hasPrev: false,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch site backups
      .addCase(fetchSiteBackups.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSiteBackups.fulfilled, (state, action) => {
        state.loading = false;
        state.backups = action.payload.backups || [];
        state.pagination = action.payload.pagination || state.pagination;
      })
      .addCase(fetchSiteBackups.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      })

      // Fetch all sites backups
      .addCase(fetchAllSitesBackups.pending, (state) => {
        state.allBackupsLoading = true;
        state.allBackupsError = null;
      })
      .addCase(fetchAllSitesBackups.fulfilled, (state, action) => {
        state.allBackupsLoading = false;
        state.allBackups = action.payload.backups || [];
        state.allBackupsPagination =
          action.payload.pagination || state.allBackupsPagination;
      })
      .addCase(fetchAllSitesBackups.rejected, (state, action) => {
        state.allBackupsLoading = false;
        state.allBackupsError = action.payload || action.error.message;
      })

      // Create backup
      .addCase(createNewBackup.pending, (state) => {
        state.createLoading = true;
        state.createError = null;
      })
      .addCase(createNewBackup.fulfilled, (state, action) => {
        state.createLoading = false;
        // Optionally refresh backups list
      })
      .addCase(createNewBackup.rejected, (state, action) => {
        state.createLoading = false;
        state.createError = action.payload || action.error.message;
      })

      // Restore backup
      .addCase(restoreSiteBackup.pending, (state) => {
        state.restoreLoading = true;
        state.restoreError = null;
      })
      .addCase(restoreSiteBackup.fulfilled, (state, action) => {
        state.restoreLoading = false;
      })
      .addCase(restoreSiteBackup.rejected, (state, action) => {
        state.restoreLoading = false;
        state.restoreError = action.payload || action.error.message;
      })

      // Delete backup
      .addCase(deleteSiteBackup.pending, (state) => {
        state.deleteLoading = true;
        state.deleteError = null;
      })
      .addCase(deleteSiteBackup.fulfilled, (state, action) => {
        state.deleteLoading = false;
        // Remove from both backups arrays
        state.backups = state.backups.filter(
          (backup) => backup.id !== action.meta.arg,
        );
        state.allBackups = state.allBackups.filter(
          (backup) => backup.id !== action.meta.arg,
        );
        // Update pagination
        if (state.pagination.totalCount > 0) {
          state.pagination.totalCount -= 1;
        }
        if (state.allBackupsPagination.totalCount > 0) {
          state.allBackupsPagination.totalCount -= 1;
        }
      })
      .addCase(deleteSiteBackup.rejected, (state, action) => {
        state.deleteLoading = false;
        state.deleteError = action.payload || action.error.message;
      })

      // Download backup
      .addCase(downloadSiteBackup.pending, (state) => {
        state.downloadLoading = true;
        state.downloadError = null;
      })
      .addCase(downloadSiteBackup.fulfilled, (state, action) => {
        state.downloadLoading = false;
      })
      .addCase(downloadSiteBackup.rejected, (state, action) => {
        state.downloadLoading = false;
        state.downloadError = action.payload || action.error.message;
      });
  },
});

export const { clearBackupState, clearBackupData } = backupSlice.actions;
export default backupSlice.reducer;
